/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 segmented control: one choice from a few (My / Team, List / Board).
 * A radio group for assistive tech; arrow keys move between options.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { v2 as t } from "styles/tokens/v2";

export default function SegmentedControl({ label, options, value, onChange }) {
  const move = (e, index) => {
    const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    onChange(next.value);
  };
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{ display: "inline-flex", p: "3px", gap: "2px", borderRadius: "10px", bgcolor: t.surface2, border: `1px solid ${t.border}` }}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        const Icon = o.icon;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => move(e, i)}
            sx={{
              height: 30,
              px: 1.25,
              gap: 0.75,
              borderRadius: "8px",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: selected ? 600 : 500,
              color: selected ? t.text : t.text2,
              bgcolor: selected ? "#FFFFFF" : "transparent",
              boxShadow: selected ? "0 1px 2px rgba(14,35,72,0.12)" : "none",
              "&:hover": { color: t.text },
              "&.Mui-focusVisible": { outline: `2px solid ${t.link}`, outlineOffset: 1 },
            }}
          >
            {Icon && <Icon size={15} aria-hidden="true" />}
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

SegmentedControl.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired, icon: PropTypes.elementType })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
